import axios, { AxiosResponse } from "axios";
import config from "../config";
import { Home, HomeCategory, FilterOptions } from "../types";

// Types for CMS API responses
export interface CmsPage {
  id: string;
  slug: string;
  title: string;
  isListed: boolean;
  fields: CmsField[] | Record<string, any>;
}

export interface CmsField {
  id: string;
  key: string;
  value: string;
  locale: string | null;
}

export interface CmsPost {
  id: string;
  slug: string;
  title: string;
  body: string;
  status: string;
  publishedAt: string;
}

// Bundle API response type
export interface CmsBundleResponse {
  settings: any;
  pages: CmsPage[];
  collections: any[];
  globalComponents: {
    header: any;
    footer: any;
    scripts: any[];
  };
  lastUpdated: string;
}

// API client instance
const apiClient = axios.create({
  baseURL: config.cmsApiUrl,
  headers: config.apiHeaders,
  timeout: 10000,
});

// Error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error.response?.data || error.message);
    throw error;
  }
);

// Page API functions
export const pageApi = {
  // Get the complete website bundle (RECOMMENDED - single request for everything)
  async getBundle(): Promise<CmsBundleResponse> {
    try {
      const response: AxiosResponse<CmsBundleResponse> = await apiClient.get(
        `/websites/${config.websiteSlug}/bundle`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching bundle:", error);
      throw error;
    }
  },

  // Get all pages for the website (legacy - use getBundle instead)
  async getAllPages(): Promise<CmsPage[]> {
    try {
      const response: AxiosResponse<CmsPage[]> = await apiClient.get(
        `/pages/${config.websiteSlug}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pages:", error);
      throw error;
    }
  },

  // Get a specific page by slug
  async getPageBySlug(slug: string): Promise<CmsPage | null> {
    try {
      const response: AxiosResponse<CmsPage> = await apiClient.get(
        `/pages/${config.websiteSlug}/${slug}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error("Error fetching page:", error);
      throw error;
    }
  },

  // Get page fields as a key-value object
  getPageFields(page: CmsPage): Record<string, string> {
    const fields: Record<string, string> = {};

    // Handle different response structures
    if (page.fields && Array.isArray(page.fields)) {
      page.fields.forEach((field) => {
        fields[field.key] = field.value;
      });
    } else if (page.fields && typeof page.fields === "object") {
      // If fields is an object instead of array
      Object.entries(page.fields).forEach(([key, value]) => {
        fields[key] = value as string;
      });
    }

    return fields;
  },
};

// Posts API functions (for future blog functionality)
export const postApi = {
  // Get all posts for the website
  async getAllPosts(): Promise<CmsPost[]> {
    try {
      const response: AxiosResponse<CmsPost[]> = await apiClient.get(
        `/posts/${config.websiteSlug}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching posts:", error);
      throw error;
    }
  },

  // Get a specific post by slug
  async getPostBySlug(slug: string): Promise<CmsPost | null> {
    try {
      const response: AxiosResponse<CmsPost> = await apiClient.get(
        `/posts/${config.websiteSlug}/${slug}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error("Error fetching post:", error);
      throw error;
    }
  },
};

// Import static homes data for synchronous access
import homesData from "../data/homes.json";

// Homes API functions - this will be used to fetch homes data from CMS
// For now, we'll keep the existing static data but structure it for future CMS integration
export const homesApi = {
  // Get all homes (synchronous version for backward compatibility)
  getAllHomes(): Home[] {
    return homesData as Home[];
  },

  // Get homes by category (synchronous version)
  getHomesByCategory(category: HomeCategory): Home[] {
    const homes = this.getAllHomes();
    return homes.filter((home) => home.category === category);
  },

  // Get home by slug (synchronous version)
  getHomeBySlug(slug: string): Home | undefined {
    const homes = this.getAllHomes();
    return homes.find((home) => home.slug === slug);
  },

  // Get featured homes (synchronous version)
  getFeaturedHomes(): Home[] {
    const homes = this.getAllHomes();
    return homes.filter((home) => home.isFeatured);
  },

  // Get related homes (synchronous version)
  getRelatedHomes(
    homeId: string,
    category: HomeCategory,
    limit: number = 3
  ): Home[] {
    const homes = this.getAllHomes();
    const currentHome = homes.find((home) => home.id === homeId);
    if (!currentHome) return [];

    // First try to get homes from related IDs
    let related: Home[] = [];
    if (currentHome.relatedHomes) {
      related = homes.filter(
        (home) =>
          currentHome.relatedHomes!.includes(home.id) && home.id !== homeId
      );
    }

    // If we need more homes, get others from the same category
    if (related.length < limit) {
      const categoryHomes = homes.filter(
        (home) =>
          home.category === category &&
          home.id !== homeId &&
          !related.some((r) => r.id === home.id)
      );
      related = [...related, ...categoryHomes];
    }

    return related.slice(0, limit);
  },

  // Filter homes (synchronous version)
  filterHomes(filters: FilterOptions): Home[] {
    const homes = this.getAllHomes();
    return homes.filter((home) => {
      if (
        filters.category &&
        filters.category !== "all" &&
        home.category !== filters.category
      ) {
        return false;
      }

      if (filters.minBedrooms && home.specs.bedrooms < filters.minBedrooms) {
        return false;
      }

      if (filters.maxBedrooms && home.specs.bedrooms > filters.maxBedrooms) {
        return false;
      }

      if (filters.minBathrooms && home.specs.bathrooms < filters.minBathrooms) {
        return false;
      }

      if (filters.maxBathrooms && home.specs.bathrooms > filters.maxBathrooms) {
        return false;
      }

      if (filters.minArea && home.specs.totalArea < filters.minArea) {
        return false;
      }

      if (filters.maxArea && home.specs.totalArea > filters.maxArea) {
        return false;
      }

      return true;
    });
  },

  // Search homes (synchronous version)
  searchHomes(query: string): Home[] {
    const homes = this.getAllHomes();
    const lowercaseQuery = query.toLowerCase();
    return homes.filter(
      (home) =>
        home.name.toLowerCase().includes(lowercaseQuery) ||
        home.keyFeatures.some((feature) =>
          feature.toLowerCase().includes(lowercaseQuery)
        ) ||
        home.category.toLowerCase().includes(lowercaseQuery)
    );
  },

  // Async versions for future CMS integration
  async getAllHomesAsync(): Promise<Home[]> {
    // In a real implementation, this would fetch from CMS
    // For now, we'll return the static data
    return homesData as Home[];
  },

  async getHomesByCategoryAsync(category: HomeCategory): Promise<Home[]> {
    const homes = await this.getAllHomesAsync();
    return homes.filter((home) => home.category === category);
  },

  async getHomeBySlugAsync(slug: string): Promise<Home | undefined> {
    const homes = await this.getAllHomesAsync();
    return homes.find((home) => home.slug === slug);
  },

  async getFeaturedHomesAsync(): Promise<Home[]> {
    const homes = await this.getAllHomesAsync();
    return homes.filter((home) => home.isFeatured);
  },

  async getRelatedHomesAsync(
    homeId: string,
    category: HomeCategory,
    limit: number = 3
  ): Promise<Home[]> {
    const homes = await this.getAllHomesAsync();
    const currentHome = homes.find((home) => home.id === homeId);
    if (!currentHome) return [];

    // First try to get homes from related IDs
    let related: Home[] = [];
    if (currentHome.relatedHomes) {
      related = homes.filter(
        (home) =>
          currentHome.relatedHomes!.includes(home.id) && home.id !== homeId
      );
    }

    // If we need more homes, get others from the same category
    if (related.length < limit) {
      const categoryHomes = homes.filter(
        (home) =>
          home.category === category &&
          home.id !== homeId &&
          !related.some((r) => r.id === home.id)
      );
      related = [...related, ...categoryHomes];
    }

    return related.slice(0, limit);
  },

  async filterHomesAsync(filters: FilterOptions): Promise<Home[]> {
    const homes = await this.getAllHomesAsync();
    return homes.filter((home) => {
      if (
        filters.category &&
        filters.category !== "all" &&
        home.category !== filters.category
      ) {
        return false;
      }

      if (filters.minBedrooms && home.specs.bedrooms < filters.minBedrooms) {
        return false;
      }

      if (filters.maxBedrooms && home.specs.bedrooms > filters.maxBedrooms) {
        return false;
      }

      if (filters.minBathrooms && home.specs.bathrooms < filters.minBathrooms) {
        return false;
      }

      if (filters.maxBathrooms && home.specs.bathrooms > filters.maxBathrooms) {
        return false;
      }

      if (filters.minArea && home.specs.totalArea < filters.minArea) {
        return false;
      }

      if (filters.maxArea && home.specs.totalArea > filters.maxArea) {
        return false;
      }

      return true;
    });
  },

  async searchHomesAsync(query: string): Promise<Home[]> {
    const homes = await this.getAllHomesAsync();
    const lowercaseQuery = query.toLowerCase();
    return homes.filter(
      (home) =>
        home.name.toLowerCase().includes(lowercaseQuery) ||
        home.keyFeatures.some((feature) =>
          feature.toLowerCase().includes(lowercaseQuery)
        ) ||
        home.category.toLowerCase().includes(lowercaseQuery)
    );
  },
};

// Email sending API
export interface SendEmailRequest {
  senderName: string;
  senderEmail: string;
  senderPhone?: string;
  message: string;
  subject?: string;
  metadata?: Record<string, any>;
}

export interface SendEmailResponse {
  success: boolean;
  message: string;
  emailId: string;
}

export const emailApi = {
  // Send email
  async sendEmail(data: SendEmailRequest): Promise<SendEmailResponse> {
    try {
      const response: AxiosResponse<SendEmailResponse> = await apiClient.post(
        '/send-email',
        {
          ...data,
          websiteSlug: config.websiteSlug,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  },
};

export interface GrowthtradiesLeadPayload {
  name: string;
  email: string;
  phone: string;
  job_description: string;
  suburb?: string;
}

export const growthtradiesApi = {
  async submitLead(data: GrowthtradiesLeadPayload): Promise<void> {
    await axios.post(
      `${config.growthtradiesApiUrl}/api/forms/${config.growthtradiesFormId}`,
      data,
      { timeout: 8000 }
    );
  },
};

// Utility function to get category display name
export const getCategoryDisplayName = (category: HomeCategory): string => {
  switch (category) {
    case "core":
      return "Core Homes";
    case "family":
      return "Family Homes";
    case "kiwiana":
      return "Kiwiana Homes";
    default:
      return category;
  }
};

export default {
  pageApi,
  postApi,
  homesApi,
  emailApi,
  getCategoryDisplayName,
};
